import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Text,
  HStack,
  Button,
  IconButton,
  Tooltip,
} from 'src/components/ui';
import { CloseIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { FaMousePointer } from 'react-icons/fa';
import {
  TOP_BAR_HEIGHT,
  RIGHT_SIDEBAR_WIDTH_COLLAPSED,
} from 'src/components/campaigns/utils/constants';

interface CampaignFormTourStep {
  fieldId: string;
  aiButtonId: string;
  goDirectlyToAi?: boolean;
  // For steps where cursor should point to a primary button first (like "View locations")
  primaryButtonSelector?: string;
  // For steps where no cursor should be shown at all
  hideCursor?: boolean;
  // For steps that don't have a text description to animate (button-only steps)
  skipTypewriter?: boolean;
  // Delay before cursor moves to primary button (ms) - makes the action clearer
  cursorAnimationDelay?: number;
  // Extra top padding for spotlight (includes title/description above the field)
  spotlightTopPadding?: number;
  // Extra bottom padding for spotlight (for dynamic content that may appear below)
  spotlightBottomPadding?: number;
  // Use base element rect only (don't expand to include all children like map cards)
  useBaseRect?: boolean;
  // Maximum height for the spotlight (prevents large dynamic content from expanding it)
  maxSpotlightHeight?: number;
  // For tall elements, scroll to show top instead of centering
  scrollToTop?: boolean;
}

const TOUR_STEPS: CampaignFormTourStep[] = [
  { fieldId: 'campaign-field-title', aiButtonId: 'campaign-ai-title' },
  { fieldId: 'campaign-field-description', aiButtonId: 'campaign-ai-description' },
  // Promoted Object: extra bottom padding for the URL input that appears when "Yes" is selected
  { fieldId: 'campaign-field-promotedObject', aiButtonId: 'campaign-ai-promotedObject', spotlightBottomPadding: 60 },
  // Customer Profiles: go directly to AI, has description to animate, extra bottom padding for tooltip spacing
  { fieldId: 'campaign-field-customerProfiles', aiButtonId: 'campaign-ai-customerProfiles', goDirectlyToAi: true, spotlightBottomPadding: 45 },
  // Locations: cursor points to "View locations" button first, limit height to avoid showing map cards, extra bottom padding for tooltip spacing
  { fieldId: 'campaign-field-locations', aiButtonId: 'campaign-ai-locations', primaryButtonSelector: 'button:has-text("View")', useBaseRect: true, maxSpotlightHeight: 130, spotlightBottomPadding: 45 },
  // Service Areas: cursor points to "View service areas" button first, limit height to avoid showing map cards, extra bottom padding for tooltip spacing
  { fieldId: 'campaign-field-serviceAreas', aiButtonId: 'campaign-ai-serviceAreas', primaryButtonSelector: 'button:has-text("View")', useBaseRect: true, maxSpotlightHeight: 130, spotlightBottomPadding: 45 },
  // Destination URL: cursor points to edit/pencil button first, with slower animation for clarity
  { fieldId: 'campaign-field-destination', aiButtonId: 'campaign-ai-destination', primaryButtonSelector: 'button[aria-label*="edit" i], button[aria-label*="Edit"], [data-testid*="edit"], button:has-text("Edit")', cursorAnimationDelay: 800 },
  // Channels: no cursor - user clicks as they want, scroll to top, use base rect, top padding to show full title
  { fieldId: 'campaign-field-channels', aiButtonId: 'campaign-ai-channels', hideCursor: true, skipTypewriter: true, scrollToTop: true, useBaseRect: true, spotlightTopPadding: 15, maxSpotlightHeight: 700 },
];

const IDLE_TIMEOUT_MS = 5000;

interface CampaignFormTourProps {
  isActive: boolean;
  onComplete: () => void;
  onSkip: () => void;
  onOpenAiForField?: (fieldName: string) => void;
}

const CampaignFormTour: React.FC<CampaignFormTourProps> = ({
  isActive,
  onComplete,
  onSkip,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [showClickEffect, setShowClickEffect] = useState(false);
  const [showAiSuggestion, setShowAiSuggestion] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [originalText, setOriginalText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [descriptionElement, setDescriptionElement] = useState<HTMLElement | null>(null);
  const [typingCursorPos, setTypingCursorPos] = useState<{ x: number; y: number } | null>(null);
  const [isDescriptionEmpty, setIsDescriptionEmpty] = useState(true);

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typewriterTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasInitializedRef = useRef(false);
  const scrollLockRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const rafIdRef = useRef<number | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const isScrollingRef = useRef(false); // Flag to pause RAF during scroll transitions
  const prevStepIndexRef = useRef<number>(-1); // Track previous step to detect back navigation

  const currentStep = TOUR_STEPS[currentStepIndex];

  // Check if we're on the description step (index 1)
  const isDescriptionStep = currentStepIndex === 1;

  // Function to check if description field has content
  const checkDescriptionContent = useCallback(() => {
    const descriptionField = document.getElementById('campaign-field-description');
    if (!descriptionField) return false;

    // Find textarea inside the description field
    const textarea = descriptionField.querySelector('textarea');
    if (textarea) {
      const hasContent = textarea.value.trim().length > 0;
      setIsDescriptionEmpty(!hasContent);
      return hasContent;
    }
    return false;
  }, []);

  // Monitor description field content when on description step
  useEffect(() => {
    if (!isActive || !isDescriptionStep) return;

    // Check immediately
    checkDescriptionContent();

    // Set up interval to check periodically
    const interval = setInterval(checkDescriptionContent, 500);

    // Also listen for input events on the textarea
    const descriptionField = document.getElementById('campaign-field-description');
    const textarea = descriptionField?.querySelector('textarea');

    const handleInput = () => {
      checkDescriptionContent();
    };

    if (textarea) {
      textarea.addEventListener('input', handleInput);
    }

    return () => {
      clearInterval(interval);
      if (textarea) {
        textarea.removeEventListener('input', handleInput);
      }
    };
  }, [isActive, isDescriptionStep, checkDescriptionContent]);

  // ============================================
  // AGGRESSIVE SCROLL BLOCKING (without position:fixed to avoid blank space)
  // ============================================
  const lockScroll = useCallback((targetPosition?: { x: number; y: number }) => {
    // Use target position if provided, otherwise save current position
    if (targetPosition) {
      scrollLockRef.current = targetPosition;
    } else {
      scrollLockRef.current = {
        x: window.pageXOffset || document.documentElement.scrollLeft || 0,
        y: window.pageYOffset || document.documentElement.scrollTop || 0,
      };
    }

    // NOTE: We intentionally DO NOT set overflow:hidden because it prevents
    // window.scrollTo from working, which breaks scroll positioning.
    // Instead, we rely on event prevention + RAF loop to maintain position.

    // Prevent all scroll-related events
    const preventDefault = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    };

    const preventKeys = (e: KeyboardEvent) => {
      // Allow keyboard input when focused on an input/textarea inside the spotlight
      const activeEl = document.activeElement;
      const isInputFocused = activeEl && (
        activeEl.tagName === 'INPUT' ||
        activeEl.tagName === 'TEXTAREA' ||
        activeEl.tagName === 'SELECT' ||
        (activeEl as HTMLElement).isContentEditable
      );

      // If user is typing in an input, allow all keys except Tab
      if (isInputFocused) {
        if (e.key === 'Tab') {
          e.preventDefault();
          return false;
        }
        return; // Allow all other keys
      }

      // Block scroll keys when not in an input
      const scrollKeys = [
        'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'PageUp', 'PageDown', 'Home', 'End', ' ', 'Tab'
      ];
      if (scrollKeys.includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Add listeners with capture phase
    const options = { passive: false, capture: true } as AddEventListenerOptions;
    window.addEventListener('wheel', preventDefault, options);
    window.addEventListener('touchmove', preventDefault, options);
    window.addEventListener('scroll', preventDefault, options);
    document.addEventListener('wheel', preventDefault, options);
    document.addEventListener('touchmove', preventDefault, options);
    document.addEventListener('scroll', preventDefault, options);
    document.addEventListener('keydown', preventKeys, { capture: true });

    // RAF loop to forcefully maintain position (skipped during scroll transitions)
    const lockPosition = () => {
      // Skip scroll reset if we're in a scroll transition
      if (!isScrollingRef.current) {
        if (window.pageYOffset !== scrollLockRef.current.y ||
            window.pageXOffset !== scrollLockRef.current.x) {
          window.scrollTo(scrollLockRef.current.x, scrollLockRef.current.y);
        }
      }
      rafIdRef.current = requestAnimationFrame(lockPosition);
    };
    rafIdRef.current = requestAnimationFrame(lockPosition);

    // Return cleanup function
    return () => {
      // Cancel RAF
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      // Remove listeners
      window.removeEventListener('wheel', preventDefault, options);
      window.removeEventListener('touchmove', preventDefault, options);
      window.removeEventListener('scroll', preventDefault, options);
      document.removeEventListener('wheel', preventDefault, options);
      document.removeEventListener('touchmove', preventDefault, options);
      document.removeEventListener('scroll', preventDefault, options);
      document.removeEventListener('keydown', preventKeys, { capture: true });

      // Note: No overflow styles to restore since we don't set them anymore
    };
  }, []);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (typewriterTimerRef.current) clearInterval(typewriterTimerRef.current);
    idleTimerRef.current = null;
    typewriterTimerRef.current = null;
  }, []);

  // ============================================
  // FIND DESCRIPTION TEXT
  // ============================================
  const findDescriptionElement = useCallback((fieldId: string): { element: HTMLElement | null; text: string } => {
    const field = document.getElementById(fieldId);
    if (!field) {
      return { element: null, text: '' };
    }


    // The requirementsLabel in StringInput is rendered as:
    // <Text fontSize="12px" color="#959595">{requirementsLabel}</Text>
    // inside a <Flex w="110%"> inside the FormLabel

    // Strategy 1: Look for elements with gray/description-like styling
    const grayTextElements = Array.from(field.querySelectorAll('[class*="chakra-text"], p, span'));
    for (const el of grayTextElements) {
      const htmlEl = el as HTMLElement;
      const text = htmlEl.textContent?.trim() || '';

      // Skip short text (labels, buttons, etc.)
      if (text.length < 20) continue;

      // Skip elements with inputs
      if (htmlEl.querySelector('input, textarea, select, button')) continue;

      // Check if this element has description-like styling (small, gray text)
      const style = window.getComputedStyle(htmlEl);
      const fontSize = parseFloat(style.fontSize);
      const isSmallText = fontSize <= 14;
      const isGrayish = style.color.includes('149') || style.color.includes('150') ||
                        style.color.includes('113') || style.color.includes('959595') ||
                        style.color.includes('718096');

      // If it looks like description text (small and/or gray)
      if (isSmallText || isGrayish) {
        // Make sure it's not a label (labels are usually bold or have specific classes)
        const isLabel = htmlEl.classList.contains('chakra-form__label') ||
                        htmlEl.tagName === 'LABEL';
        if (!isLabel) {
          return { element: htmlEl, text };
        }
      }
    }

    // Strategy 2: Look specifically in FormLabel for description text
    const formLabel = field.querySelector('.chakra-form__label, label');
    if (formLabel) {
      // Find Text elements inside that look like descriptions
      const textsInLabel = Array.from(formLabel.querySelectorAll('[class*="chakra-text"], p, span'));
      for (const el of textsInLabel) {
        const htmlEl = el as HTMLElement;
        const text = htmlEl.textContent?.trim() || '';

        // Requirements label is typically longer than field label
        if (text.length > 30 && text.length < 500) {
          // Check it's not the main label text
          const parent = htmlEl.parentElement;
          if (parent && !parent.classList.contains('chakra-form__label')) {
            return { element: htmlEl, text };
          }
        }
      }

      // If the FormLabel has nested text with description, find it
      const labelText = formLabel.textContent?.trim() || '';
      // The pattern is usually "Field name*Description text here"
      if (labelText.includes('*')) {
        const parts = labelText.split('*');
        if (parts.length > 1) {
          const descPart = parts.slice(1).join('*').trim();
          if (descPart.length > 20) {
            // Find the element that contains this text
            const allTexts = Array.from(formLabel.querySelectorAll('*'));
            for (const el of allTexts) {
              if (el.textContent?.trim() === descPart) {
                return { element: el as HTMLElement, text: descPart };
              }
            }
          }
        }
      }
    }

    // Strategy 3: Look for any text element that's a reasonable description length
    const allTextElements = Array.from(field.querySelectorAll('p, span, div'));
    let bestMatch: HTMLElement | null = null;
    let bestText = '';

    for (const el of allTextElements) {
      const htmlEl = el as HTMLElement;
      const text = htmlEl.textContent?.trim() || '';

      // Skip if too short or too long
      if (text.length < 30 || text.length > 500) continue;

      // Skip if it has interactive children
      if (htmlEl.querySelector('input, textarea, select, button')) continue;

      // Skip if it's nested in another text element we might select
      const parent = htmlEl.parentElement;
      if (parent && ['P', 'SPAN', 'DIV'].includes(parent.tagName) &&
          !parent.querySelector('input, textarea, select, button')) {
        // Prefer the parent if it contains similar text
        continue;
      }

      // Prefer longer descriptions
      if (text.length > bestText.length) {
        bestMatch = htmlEl;
        bestText = text;
      }
    }

    if (bestMatch) {
      return { element: bestMatch, text: bestText };
    }

    return { element: null, text: '' };
  }, []);

  // ============================================
  // TYPEWRITER
  // ============================================
  const startTypewriter = useCallback((element: HTMLElement, text: string) => {
    setOriginalText(text);
    setDescriptionElement(element);
    setIsTyping(true);
    element.textContent = '';

    // Update cursor position based on actual text width, not container width
    const updateCursorPos = () => {
      // Use Range API to get the exact position of the text end
      const textNode = element.firstChild;
      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        const range = document.createRange();
        range.setStart(textNode, textNode.textContent?.length || 0);
        range.setEnd(textNode, textNode.textContent?.length || 0);
        const rangeRect = range.getBoundingClientRect();
        if (rangeRect.x > 0) {
          setTypingCursorPos({ x: rangeRect.x + 2, y: rangeRect.top });
          return;
        }
      }
      // Fallback: use element's left + scrollWidth (actual text width)
      const rect = element.getBoundingClientRect();
      const textWidth = element.scrollWidth;
      setTypingCursorPos({ x: rect.left + textWidth + 2, y: rect.top });
    };
    updateCursorPos();

    let index = 0;
    typewriterTimerRef.current = setInterval(() => {
      if (index < text.length) {
        element.textContent = text.slice(0, index + 1);
        index++;
        // Update cursor position after each character
        updateCursorPos();
      } else {
        if (typewriterTimerRef.current) clearInterval(typewriterTimerRef.current);
        setIsTyping(false);
        setTypingCursorPos(null);
      }
    }, 15);
  }, []);

  // ============================================
  // IDLE TIMER
  // ============================================
  const startIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (currentStep?.goDirectlyToAi) return;

    idleTimerRef.current = setTimeout(() => {
      setShowAiSuggestion(true);
      const aiBtn = document.getElementById(currentStep?.aiButtonId || '');
      if (aiBtn) {
        const aiRect = aiBtn.getBoundingClientRect();
        // Position cursor centered on the AI button
        setCursorPosition({
          x: aiRect.left + aiRect.width / 2 - 12,
          y: aiRect.top + aiRect.height / 2 - 12
        });
        setTimeout(() => {
          setShowClickEffect(true);
          setTimeout(() => setShowClickEffect(false), 500);
        }, 600);
      }
    }, IDLE_TIMEOUT_MS);
  }, [currentStep]);

  // ============================================
  // FIND INPUT ELEMENT FOR CURSOR POSITIONING
  // ============================================
  const findInputElement = useCallback((fieldId: string): HTMLElement | null => {
    const field = document.getElementById(fieldId);
    if (!field) return null;

    // Look for input, textarea, or select inside the field
    const input = field.querySelector('input, textarea, select');
    if (input) return input as HTMLElement;

    // If no input found, return the field itself
    return field;
  }, []);

  const resetIdleTimer = useCallback(() => {
    setShowAiSuggestion(false);
    // Position cursor back to the input element
    if (currentStep) {
      const inputEl = findInputElement(currentStep.fieldId);
      if (inputEl) {
        const inputRect = inputEl.getBoundingClientRect();
        setCursorPosition({
          x: inputRect.left + 20,
          y: inputRect.top + inputRect.height / 2 + 5
        });
      }
    }
    startIdleTimer();
  }, [startIdleTimer, currentStep, findInputElement]);

  // ============================================
  // FIND PRIMARY BUTTON FOR CURSOR
  // ============================================
  const findPrimaryButton = useCallback((fieldId: string, selector?: string): HTMLElement | null => {
    if (!selector) {
      return null;
    }

    const field = document.getElementById(fieldId);
    if (!field) {
      return null;
    }

    // Try the CSS selector first
    try {
      // Split selector by comma and try each one
      const selectors = selector.split(',').map(s => s.trim());
      for (const sel of selectors) {
        // Handle :has-text pseudo selector (not standard CSS)
        if (sel.includes(':has-text')) {
          const textMatch = sel.match(/:has-text\("([^"]+)"\)/);
          if (textMatch) {
            const text = textMatch[1];
            const buttons = Array.from(field.querySelectorAll('button'));
            for (const btn of buttons) {
              if (btn.textContent?.toLowerCase().includes(text.toLowerCase())) {
                return btn as HTMLElement;
              }
            }
          }
        } else if (sel.includes('[aria-label')) {
          // Handle aria-label selectors with case-insensitive matching
          const ariaMatch = sel.match(/\[aria-label[*^$]?=["']([^"']+)["']/i);
          if (ariaMatch) {
            const ariaValue = ariaMatch[1].toLowerCase();
            const buttons = Array.from(field.querySelectorAll('button'));
            for (const btn of buttons) {
              const btnAriaLabel = btn.getAttribute('aria-label')?.toLowerCase() || '';
              if (btnAriaLabel.includes(ariaValue) || btnAriaLabel === ariaValue) {
                return btn as HTMLElement;
              }
            }
          }
        } else {
          const el = field.querySelector(sel);
          if (el) return el as HTMLElement;
        }
      }
    } catch {
      // If selector is invalid, continue to fallback
    }

    return null;
  }, []);

  // ============================================
  // CURSOR
  // ============================================
  const animateCursor = useCallback((fieldId: string, wrapperRect: DOMRect, primaryButtonSelector?: string, animationDelay?: number) => {
    // Check if there's a primary button to point to first
    const primaryBtn = findPrimaryButton(fieldId, primaryButtonSelector);

    // Use custom delay if provided, otherwise default to 100ms
    const delay = animationDelay || 100;

    if (primaryBtn) {
      // Point to primary button first, then after idle timeout will go to AI button
      const btnRect = primaryBtn.getBoundingClientRect();
      setCursorPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
      setTimeout(() => {
        const newPos = {
          x: btnRect.left + btnRect.width / 2 - 12,
          y: btnRect.top + btnRect.height / 2 - 12
        };
        setCursorPosition(newPos);
        setTimeout(() => {
          setShowClickEffect(true);
          setTimeout(() => setShowClickEffect(false), 500);
        }, 600);
      }, delay);
    } else {
      // Find the actual input element for better cursor positioning
      const inputEl = findInputElement(fieldId);
      const inputRect = inputEl ? inputEl.getBoundingClientRect() : wrapperRect;

      setCursorPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
      setTimeout(() => {
        // Position cursor on the input element, slightly offset into it
        setCursorPosition({
          x: inputRect.left + 20,
          y: inputRect.top + inputRect.height / 2 + 5
        });
        setTimeout(() => {
          setShowClickEffect(true);
          setTimeout(() => setShowClickEffect(false), 500);
        }, 600);
      }, 100);
    }
  }, [findInputElement, findPrimaryButton]);

  // ============================================
  // CALCULATE UNION BOUNDING BOX
  // ============================================
  const calculateUnionRect = useCallback((element: HTMLElement): DOMRect => {
    // Get all visible child elements and calculate a union bounding box
    const baseRect = element.getBoundingClientRect();

    // Get all text and input elements inside
    const children = element.querySelectorAll('*');
    let minLeft = baseRect.left;
    let minTop = baseRect.top;
    let maxRight = baseRect.right;
    let maxBottom = baseRect.bottom;

    children.forEach((child) => {
      const childRect = child.getBoundingClientRect();
      // Only include visible elements with actual dimensions
      if (childRect.width > 0 && childRect.height > 0) {
        minLeft = Math.min(minLeft, childRect.left);
        minTop = Math.min(minTop, childRect.top);
        maxRight = Math.max(maxRight, childRect.right);
        maxBottom = Math.max(maxBottom, childRect.bottom);
      }
    });

    // Create a new DOMRect-like object with the union dimensions
    return new DOMRect(minLeft, minTop, maxRight - minLeft, maxBottom - minTop);
  }, []);

  // ============================================
  // SCROLL TO POSITION ELEMENT IN VIEWPORT
  // ============================================
  const scrollToCenter = useCallback((element: HTMLElement, extraTopPadding = 0, scrollToTop = false): Promise<{ targetScrollY: number }> => {
    return new Promise((resolve) => {
      // Calculate the scroll position to center the element in viewport
      const rect = element.getBoundingClientRect();
      const elementTop = rect.top + window.pageYOffset;
      const elementHeight = rect.height;
      const viewportHeight = window.innerHeight;

      let targetScrollY: number;

      if (scrollToTop) {
        // For tall elements, scroll so the TOP of the element is visible with padding from viewport top
        // Position element about 160px from top of viewport (leaves room for title to be fully visible)
        // Account for extraTopPadding to include title above the element
        const topOffset = 160;
        const adjustedElementTop = elementTop - extraTopPadding;
        targetScrollY = Math.max(0, adjustedElementTop - topOffset);
      } else {
        // When we have extra top padding (like for channels step), we need to scroll
        // higher up so that the padding area (which contains the title) is visible
        // Subtract extraTopPadding from elementTop to account for the spotlight extension
        const adjustedElementTop = elementTop - extraTopPadding;
        const adjustedHeight = elementHeight + extraTopPadding;

        // Calculate where to scroll so element (including extra padding) is centered
        targetScrollY = Math.max(0, adjustedElementTop - (viewportHeight / 2) + (adjustedHeight / 2));
      }

      // Clean up any existing scroll lock first (stops RAF and removes event listeners)
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      // Use requestAnimationFrame to ensure DOM has processed any changes
      // after cleanup
      requestAnimationFrame(() => {
        // Double RAF to ensure styles are fully applied
        requestAnimationFrame(() => {
          // Scroll FIRST while overflow is not hidden
          window.scrollTo({
            top: targetScrollY,
            left: 0,
            behavior: 'instant' as ScrollBehavior
          });

          // Now set up scroll lock to maintain this position
          // This will apply overflow:hidden and start RAF loop
          cleanupRef.current = lockScroll({ x: 0, y: targetScrollY });

          // Additional RAF to verify position is maintained
          requestAnimationFrame(() => {
            resolve({ targetScrollY });
          });
        });
      });
    });
  }, [lockScroll]);

  // ============================================
  // INITIALIZE STEP
  // ============================================
  // Accept optional stepIndex to avoid closure issues when restarting
  const initializeStep = useCallback(async (stepIndex?: number) => {
    const idx = stepIndex ?? currentStepIndex;
    const step = TOUR_STEPS[idx];

    if (!step) return;


    clearTimers();
    setShowAiSuggestion(false);
    setOriginalText('');
    setDescriptionElement(null);

    // Always use the field element for scroll and spotlight (not the AI button)
    const fieldElement = document.getElementById(step.fieldId);

    if (!fieldElement) {
      console.warn('[CampaignFormTour] Field element not found:', step.fieldId);
      return;
    }

    // First, scroll the FIELD to position in viewport
    // Pass spotlightTopPadding so scroll accounts for the extra space at top
    // Pass scrollToTop for tall elements that should be positioned at top of viewport
    const { targetScrollY } = await scrollToCenter(fieldElement, step.spotlightTopPadding || 0, step.scrollToTop || false);

    // Now calculate rect based on the FIELD after scroll is complete

    // Use base rect only if step specifies it (avoids including map cards, etc.)
    let rect: DOMRect;
    if (step.useBaseRect) {
      rect = fieldElement.getBoundingClientRect();
    } else {
      rect = calculateUnionRect(fieldElement);
    }

    // Apply max height constraint if specified
    if (step.maxSpotlightHeight && rect.height > step.maxSpotlightHeight) {
      rect = new DOMRect(rect.x, rect.y, rect.width, step.maxSpotlightHeight);
    }

    setTargetRect(rect);
    setIsVisible(true);

    // Small delay then fade in (end transition)
    setTimeout(() => {
      setIsTransitioning(false);

      // For hideCursor steps, skip cursor animation entirely
      if (step.hideCursor) {
        // No cursor for this step - user clicks as they want
        setCursorPosition({ x: -100, y: -100 }); // Hide cursor off-screen
        return;
      }

      // For goDirectlyToAi steps, position cursor on the AI button immediately
      if (step.goDirectlyToAi) {
        const aiBtn = document.getElementById(step.aiButtonId);
        if (aiBtn) {
          const aiRect = aiBtn.getBoundingClientRect();
          // Start cursor from center of screen, then move to AI button
          setCursorPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
          setTimeout(() => {
            setCursorPosition({
              x: aiRect.left + aiRect.width / 2 - 12,
              y: aiRect.top + aiRect.height / 2 - 12
            });
            setTimeout(() => {
              setShowClickEffect(true);
              setTimeout(() => setShowClickEffect(false), 500);
            }, 600);
          }, 100);
        }
        // Show AI suggestion immediately for goDirectlyToAi steps
        setShowAiSuggestion(true);
      } else {
        // Pass primaryButtonSelector and cursorAnimationDelay for steps that need it
        animateCursor(step.fieldId, rect, step.primaryButtonSelector, step.cursorAnimationDelay);
      }
    }, 50);

    // Delay finding description to ensure DOM is fully rendered (skip for button-only steps)
    if (!step.skipTypewriter) {
      setTimeout(() => {
        const { element: descEl, text } = findDescriptionElement(step.fieldId);
        if (descEl && text) {
          startTypewriter(descEl, text);
        }
      }, 150);
    }

    if (!step.goDirectlyToAi && !step.hideCursor) {
      startIdleTimer();
    }
  }, [currentStepIndex, clearTimers, scrollToCenter, calculateUnionRect, animateCursor, findDescriptionElement, startTypewriter, startIdleTimer]);

  // ============================================
  // NAVIGATION
  // ============================================
  const restoreText = useCallback(() => {
    if (descriptionElement && originalText) {
      descriptionElement.textContent = originalText;
    }
  }, [descriptionElement, originalText]);

  const handleContinue = useCallback(() => {
    restoreText();
    clearTimers();

    // Start fade-out transition
    setIsTransitioning(true);

    // Wait for fade-out, then change step
    setTimeout(() => {
      if (currentStepIndex < TOUR_STEPS.length - 1) {
        setCurrentStepIndex(prev => prev + 1);
      } else {
        onComplete();
      }
    }, 200);
  }, [currentStepIndex, clearTimers, onComplete, restoreText]);

  const handleBack = useCallback(() => {
    restoreText();
    clearTimers();

    // Start fade-out transition
    setIsTransitioning(true);

    // Wait for fade-out, then change step
    setTimeout(() => {
      if (currentStepIndex > 0) {
        setCurrentStepIndex(prev => prev - 1);
      }
    }, 200);
  }, [currentStepIndex, clearTimers, restoreText]);

  const handleSkip = useCallback(() => {
    restoreText();
    clearTimers();
    setIsVisible(false);
    onSkip();
  }, [clearTimers, onSkip, restoreText]);

  // ============================================
  // LIFECYCLE
  // ============================================
  useEffect(() => {
    if (isActive) {
      if (hasInitializedRef.current) return;
      hasInitializedRef.current = true;


      // Don't lock scroll immediately - let initializeStep handle scroll-to-center first
      setCurrentStepIndex(0);

      // Delay for first step to ensure DOM is ready
      // Pass explicit stepIndex (0) to avoid closure capturing stale state
      setTimeout(() => {
        initializeStep(0);
      }, 300);
    } else {
      restoreText();
      hasInitializedRef.current = false;
      prevStepIndexRef.current = -1; // Reset for clean restart
      setIsVisible(false);
      clearTimers();

      // Reset step index to 0 for clean restart
      setCurrentStepIndex(0);

      // Cleanup scroll lock
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    }

    return () => {
      restoreText();
      clearTimers();
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [isActive]); // Removed dependencies that cause re-runs

  // Handle step changes (after initial)
  useEffect(() => {
    // Only trigger on actual step changes (not initial setup)
    // This allows going back to step 0 which was previously broken
    if (isActive && hasInitializedRef.current && prevStepIndexRef.current !== currentStepIndex && prevStepIndexRef.current !== -1) {
      initializeStep();
    }
    prevStepIndexRef.current = currentStepIndex;
  }, [currentStepIndex, isActive, initializeStep]);

  // Reset idle timer on key press
  useEffect(() => {
    if (!isActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (!['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', ' '].includes(e.key)) {
        resetIdleTimer();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isActive, resetIdleTimer]);

  // ============================================
  // RENDER
  // ============================================
  if (!isActive || !isVisible || !targetRect) return null;

  const padding = 12;
  // Apply extra top padding for steps that need it (like channels step to show title)
  const extraTopPadding = currentStep?.spotlightTopPadding || 0;
  // Apply extra bottom padding for steps with dynamic content (like promotedObject with URL input)
  const extraBottomPadding = currentStep?.spotlightBottomPadding || 0;
  const rect = {
    left: targetRect.left - padding,
    top: targetRect.top - padding - extraTopPadding,
    right: targetRect.right + padding,
    bottom: targetRect.bottom + padding + extraBottomPadding -20,
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2 + extraTopPadding + extraBottomPadding -20,
  };

  // Calculate spotlight position relative to content area (below TopBar)
  const topBarHeight = 72; // TOP_BAR_HEIGHT numeric value
  const spotTop = Math.max(0, rect.top - topBarHeight);
  const spotBottom = Math.max(0, rect.bottom - topBarHeight);

  return createPortal(
    <Box>
      {/* Dark overlay with spotlight cutout - click blocks OUTSIDE spotlight */}
      {/* NOTE: This overlay stays visible during transitions to avoid flash */}
      {/* Starts below TopBar, stops before right sidebar to avoid double overlay */}
      <Box
        position="fixed"
        top={TOP_BAR_HEIGHT}
        left={0}
        right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        bottom={0}
        zIndex={9990}
        bg="blackAlpha.600"
        backdropFilter="blur(4px)"
        cursor="not-allowed"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onWheel={(e) => { e.preventDefault(); e.stopPropagation(); }}
        style={{
          clipPath: isTransitioning ? undefined : `polygon(
            0% 0%,
            0% 100%,
            ${rect.left}px 100%,
            ${rect.left}px ${spotTop}px,
            ${rect.right}px ${spotTop}px,
            ${rect.right}px ${spotBottom}px,
            ${rect.left}px ${spotBottom}px,
            ${rect.left}px 100%,
            100% 100%,
            100% 0%
          )`,
        }}
      />

      {/* Dark overlay for TopBar - same style as rest of overlay */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        h={TOP_BAR_HEIGHT}
        zIndex={10202}
        bg="blackAlpha.600"
        backdropFilter="blur(4px)"
        pointerEvents="none"
      />

      {/* Dark overlay for Right Sidebar - covers entire sidebar height */}
      <Box
        position="fixed"
        top={0}
        right={0}
        bottom={0}
        w={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        zIndex={10202}
        bg="blackAlpha.600"
        backdropFilter="blur(4px)"
        pointerEvents="none"
      />

      {/* Transitioning content - spotlight, tooltip, cursor */}
      <Box
        opacity={isTransitioning ? 0 : 1}
        transition="opacity 0.2s ease-in-out"
      >

      {/* Orange border around spotlight */}
      <Box
        position="fixed"
        left={`${rect.left}px`}
        top={`${rect.top}px`}
        width={`${rect.width}px`}
        height={`${rect.height}px`}
        borderRadius="8px"
        zIndex={9992}
        pointerEvents="none"
        border="3px solid"
        borderColor="orange.400"
        boxShadow="0 0 0 3px rgba(246, 133, 30, 0.25)"
      />

      {/* Step number badge - top left corner */}
      <Box
        position="fixed"
        left={`${rect.left - 12}px`}
        top={`${rect.top - 12}px`}
        zIndex={9998}
        bg="orange.500"
        color="white"
        borderRadius="full"
        w="28px"
        h="28px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="sm"
        fontWeight="bold"
        boxShadow="md"
      >
        {currentStepIndex + 1}
      </Box>

      {/* Step counter & X button - positioned inside spotlight at top-right corner */}
      <HStack
        position="fixed"
        left={`${Math.min(rect.right - 53, window.innerWidth - 90)}px`}
        top={`${rect.top - 45}px`}
        zIndex={9998}
        spacing={2}
        bg="rgba(0, 0, 0, 0.7)"
        borderRadius="md"
        px={2}
        py={1}
      >
        <Text fontSize="xs" color="white" fontWeight="500">
          {currentStepIndex + 1}/{TOUR_STEPS.length}
        </Text>
        <IconButton
          aria-label="Close tour"
          icon={<CloseIcon boxSize={2.5} color="white" />}
          size="xs"
          variant="ghost"
          display="flex"
          alignItems="center"
          justifyContent="center"
          minW="auto"
          color="white"
          _hover={{ bg: 'transparent', cursor: 'pointer' }}
          onClick={handleSkip}
        />
      </HStack>

      {/* Navigation buttons - bottom */}
      <HStack
        position="fixed"
        left={`${rect.left}px`}
        top={`${rect.bottom + 8}px`}
        width={`${rect.width}px`}
        zIndex={9998}
        justify="space-between"
        bg="rgba(0, 0, 0, 0.7)"
        borderRadius="md"
        p={1}
      >
        <HStack spacing={1}>
          {currentStepIndex > 0 && (
            <Button
              size="sm"
              variant="ghost"
              display="flex"
              alignItems="center"
              px={2}
              fontWeight="normal"
              leftIcon={<ChevronLeftIcon color="white" />}
              onClick={handleBack}
              color="white"
              _hover={{ bg: 'whiteAlpha.300' }}
            >
              Back
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            px={2}
            fontWeight="normal"
            onClick={handleSkip}
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
          >
            Skip
          </Button>
        </HStack>

        <HStack spacing={3}>
          {/* Validation message for description step */}
          {isDescriptionStep && isDescriptionEmpty && (
            <Text fontSize="xs" color="orange.200" fontStyle="italic">
              Please fill in the description
            </Text>
          )}
          <Button
            size="sm"
            colorScheme="orange"
            rightIcon={<ChevronRightIcon />}
            onClick={handleContinue}
            isDisabled={isDescriptionStep && isDescriptionEmpty}
            _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
          >
            {currentStepIndex === TOUR_STEPS.length - 1 ? 'Finish' : 'Continue'}
          </Button>
        </HStack>
      </HStack>

      {/* AI suggestion - positioned below the AI button */}
      {showAiSuggestion && (
        <Box
          position="fixed"
          left={`${cursorPosition.x - 50}px`}
          top={`${cursorPosition.y + 35}px`}
          zIndex={9998}
          bg="orange.500"
          borderRadius="md"
          px={3}
          py={1.5}
          boxShadow="md"
          _after={{
            content: '""',
            position: 'absolute',
            top: '-6px',
            left: '50%',
            transform: 'translateX(-50%)',
            borderWidth: '0 6px 6px 6px',
            borderStyle: 'solid',
            borderColor: 'transparent transparent var(--chakra-colors-orange-500) transparent',
          }}
        >
          <Text fontSize="xs" color="white" fontWeight="500" whiteSpace="nowrap">
            Get inspired by AI
          </Text>
        </Box>
      )}

      {/* Animated cursor */}
      <Box
        position="fixed"
        left={`${cursorPosition.x}px`}
        top={`${cursorPosition.y}px`}
        zIndex={9999}
        pointerEvents="none"
        transition="left 0.6s ease, top 0.6s ease"
      >
        <Box
          color="orange.500"
          filter="drop-shadow(0 2px 4px rgba(0,0,0,0.3))"
          transform={showClickEffect ? 'scale(0.85)' : 'scale(1)'}
          transition="transform 0.1s"
        >
          <FaMousePointer size={24} />
        </Box>
        {showClickEffect && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            w="30px"
            h="30px"
            borderRadius="50%"
            bg="orange.400"
            sx={{
              animation: 'ripple 0.5s ease-out',
              '@keyframes ripple': {
                '0%': { transform: 'translate(-50%, -50%) scale(0)', opacity: 0.6 },
                '100%': { transform: 'translate(-50%, -50%) scale(2)', opacity: 0 },
              },
            }}
          />
        )}
      </Box>

      {/* Typing cursor */}
      {isTyping && typingCursorPos && (
        <Box
          position="fixed"
          left={`${typingCursorPos.x}px`}
          top={`${typingCursorPos.y}px`}
          zIndex={9995}
          pointerEvents="none"
        >
          <Box
            w="2px"
            h="16px"
            bg="orange.500"
            sx={{
              animation: 'blink 0.7s infinite',
              '@keyframes blink': {
                '0%, 50%': { opacity: 1 },
                '51%, 100%': { opacity: 0 },
              },
            }}
          />
        </Box>
      )}
      </Box>
    </Box>,
    document.body
  );
};

export default CampaignFormTour;
